import { defineStore } from 'pinia';

const STORAGE_KEY = 'presentei_customers_v2';

const defaultState = () => ({
  // Lista de customers salvos (vários da família podem usar o mesmo dispositivo)
  customers: [],

  // Customer ativo para a compra corrente (pode ser um da lista ou um temporário)
  activeCustomer: null,

  payment: {
    event_id: null,
    method: null, // 'pix' | 'card'
    status: null, // 'pending' | 'confirmed' | 'failed'
    data: {
      payment_id: null,
      qrcode_image: null,
      pix_key: null,
      expires_at: null,
    },
  },
});

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      // Migração: verificar formato antigo
      const oldRaw = localStorage.getItem('presentei_customer');
      if (oldRaw) {
        const old = JSON.parse(oldRaw);
        if (old?.customer?.cpf_cnpj) {
          const state = defaultState();
          state.customers = [old.customer];
          state.payment = old.payment || state.payment;
          return state;
        }
      }
      return defaultState();
    }
    return { ...defaultState(), ...JSON.parse(raw) };
  } catch {
    return defaultState();
  }
}

function saveToStorage(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      customers:      state.customers,
      activeCustomer: state.activeCustomer,
      payment:        state.payment,
    }));
  } catch (e) {
    console.error('[customerStore] Erro ao salvar no localStorage:', e);
  }
}

export const useCustomerStore = defineStore('customer', {
  state: () => loadFromStorage(),

  getters: {
    // Mantido para compatibilidade histórica
    hasCustomer:   (state) => !!state.activeCustomer?.cpf_cnpj,
    hasCustomers:  (state) => state.customers.length > 0,

    hasPendingPayment: (state) => (eventId) =>
      state.payment?.event_id === eventId &&
      state.payment?.status === 'pending' &&
      !!state.payment?.data?.payment_id,

    // Atalho para código que ainda usa .customer
    customer: (state) => state.activeCustomer,
  },

  actions: {
    /** Adiciona ou atualiza um customer na lista e o define como ativo */
    addOrUpdateCustomer(c) {
      const idx = this.customers.findIndex(s => s.cpf_cnpj === c.cpf_cnpj);
      if (idx >= 0) {
        this.customers[idx] = { ...this.customers[idx], ...c };
      } else {
        this.customers.push({ ...c });
      }
      this.activeCustomer = { ...c };
      saveToStorage(this.$state);
    },

    /** Define o customer ativo sem alterar a lista (seleção de um já salvo) */
    setActiveCustomer(c) {
      this.activeCustomer = { ...c };
      saveToStorage(this.$state);
    },

    /** Remove customers da lista pelo array de cpf_cnpj */
    removeCustomers(cpfCnpjList) {
      this.customers = this.customers.filter(c => !cpfCnpjList.includes(c.cpf_cnpj));
      // Se o ativo foi removido, limpar
      if (this.activeCustomer && cpfCnpjList.includes(this.activeCustomer.cpf_cnpj)) {
        this.activeCustomer = null;
      }
      saveToStorage(this.$state);
    },

    setPayment({ event_id, method, status, data }) {
      this.payment = {
        event_id,
        method,
        status,
        data: { ...defaultState().payment.data, ...data },
      };
      saveToStorage(this.$state);
    },

    updatePaymentStatus(status) {
      this.payment.status = status;
      saveToStorage(this.$state);
    },

    clearPayment() {
      this.payment = defaultState().payment;
      saveToStorage(this.$state);
    },

    clearAll() {
      const clean = defaultState();
      this.customers      = clean.customers;
      this.activeCustomer = clean.activeCustomer;
      this.payment        = clean.payment;
      localStorage.removeItem(STORAGE_KEY);
    },

    // Alias legado
    setCustomer(c) {
      this.addOrUpdateCustomer(c);
    },
  },
});
